/**
 * Hook de test d’un prompt.
 *
 * Ce hook prépare les variables dynamiques du prompt, gère les documents associés,
 * exécute un ou deux appels API selon les modèles sélectionnés, puis alimente
 * la conversation de test avec les réponses obtenues.
 */

import { useAskPromptMutation } from 'core/api/ask'
import { useLazyGetDocumentDownloadBase64Query } from 'core/api/document'
import { toast } from 'core/components/common/Toast'
import { UserContext } from 'core/contexts/UserContext'
import { useProcessFile } from 'core/hooks/useProcessFile'
import { IModeleAnswer, IPromptAskResponse } from 'core/interfaces/Ask'
import { UserType } from 'core/interfaces/Conversation'
import { IDocument } from 'core/interfaces/Document'
import { PromptForm } from 'core/interfaces/Prompt'
import {
  isRtkQueryHttpError,
  messageFromApiValidationBody,
} from 'core/utils/apiValidationError'
import { usePromptConversation } from 'lauria/contexts/PromptConversationContext'
import React, { useContext, useEffect, useState } from 'react'
import {
  SubmitHandler,
  useController,
  useFieldArray,
  useForm,
  UseFormWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export type PromptTestFormData = {
  fields: { name: string; value: string }[]
  modele1: string
  modele2?: string
}

type PromptModelKey = 'modele1' | 'modele2'

type PromptModelRequest = {
  key: PromptModelKey
  modeleName: string
}

type PromptBodyBase = {
  idClient: number
  prompt: string
  temperature: number
  webSearch: string
  documents: string[]
}

/**
 * Échappe une chaîne destinée à être injectée dans une RegExp.
 *
 * Utile ici car les variables de prompt contiennent des caractères spéciaux,
 * par exemple "{client}", qui ne doivent pas être interprétés comme une syntaxe RegExp.
 */
const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Extrait les variables uniques présentes dans un prompt.
 *
 * Exemple :
 * "Bonjour {client}, dossier {dossier}" devient ["{client}", "{dossier}"].
 */
const extractVariablesFromPrompt = (prompt: string): string[] => {
  const regex = /\{[a-zA-Z0-9]+\}/g
  const matches = prompt.match(regex) ?? []

  // Le Set évite de générer plusieurs champs pour une même variable répétée dans le prompt.
  return Array.from(new Set(matches.map((match) => match.trim())))
}

export const usePromptTest = (
  watchPromptForm: UseFormWatch<PromptForm>,
  promptDocuments: IDocument[],
  promptNewFiles: File[],
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const [askPrompt] = useAskPromptMutation()
  const [fetchDocumentBase64] = useLazyGetDocumentDownloadBase64Query()
  const { convertToBase64 } = useProcessFile()
  const [variables, setVariables] = useState<string[]>([])
  const [documentsTest, setDocumentsTest] = useState<IDocument[]>([])
  const { setConversation } = usePromptConversation()
  const { t } = useTranslation()

  const { user: currentUser } = useContext(UserContext)

  const promptValue = watchPromptForm('prompt') || ''
  const modeleValue = watchPromptForm('modele') || ''
  const temperatureValue = watchPromptForm('temperature') ?? 0.5
  const webSearchValue = watchPromptForm('webSearch') ? 'true' : 'false'

  const { control, handleSubmit, register, getValues, setValue } =
    useForm<PromptTestFormData>({
      defaultValues: {
        fields: extractVariablesFromPrompt(promptValue).map((field) => ({
          name: field,
          value: '',
        })),
        modele1: modeleValue,
        modele2: '',
      },
    })

  const { fields, replace } = useFieldArray({
    control,
    name: 'fields',
  })

  const modele1Field = useController({
    name: 'modele1',
    control,
    rules: { required: true },
  }).field

  useController({
    name: 'modele2',
    control,
    rules: { required: false },
  })

  /**
   * Rafraîchit manuellement la liste des variables détectées dans le prompt courant.
   *
   * Cette méthode est exposée par le hook pour permettre à un composant consommateur
   * de forcer une synchronisation si nécessaire.
   */
  const refreshVars = (): void => {
    setVariables(extractVariablesFromPrompt(promptValue))
  }

  /**
   * Affiche un toast d'erreur uniquement pour les erreurs de validation API 422.
   *
   * Le Set permet d'éviter d'afficher plusieurs fois le même message lorsque
   * plusieurs appels API échouent avec la même erreur de validation.
   */
  const displayValidationToastIfNeeded = (
    error: unknown,
    displayedValidationMessages: Set<string>
  ): void => {
    if (!isRtkQueryHttpError(error) || error.status !== 422) {
      return
    }

    const message = messageFromApiValidationBody(error.data)

    if (message && !displayedValidationMessages.has(message)) {
      displayedValidationMessages.add(message)
      toast.error(message)
    }
  }

  /**
   * Remplace les variables du prompt par les valeurs saisies dans le formulaire de test.
   *
   * Exemple :
   * Prompt : "Bonjour {client}"
   * Field : { name: "{client}", value: "Lauria" }
   * Résultat : "Bonjour Lauria"
   */
  const buildPromptText = (
    initialPrompt: string,
    fieldsData: PromptTestFormData['fields']
  ): string => {
    return fieldsData.reduce((currentText, field) => {
      const regex = new RegExp(escapeRegExp(field.name), 'g')

      return currentText.replace(regex, field.value || '')
    }, initialPrompt)
  }

  /**
   * Fusionne les documents déjà associés au prompt avec les documents ajoutés au test.
   *
   * La méthode déduplique les documents par id lorsqu'ils existent déjà côté backend,
   * ou par nom lorsqu'il s'agit de fichiers locaux.
   */
  const mergeDocuments = (): IDocument[] => {
    const filesByName = new Map(promptNewFiles.map((file) => [file.name, file]))
    const documentsByKey = new Map<string, IDocument>()

    const allDocuments: IDocument[] = [
      ...promptDocuments,
      ...documentsTest,
    ].map((doc) => {
      if (doc.file) {
        return doc
      }

      const file = filesByName.get(doc.nomDocument)

      return file ? { ...doc, file } : doc
    })

    for (const doc of allDocuments) {
      const key =
        typeof doc.id === 'number' ? `id:${doc.id}` : `name:${doc.nomDocument}`

      const existingDocument = documentsByKey.get(key)

      // On privilégie la version qui contient le File afin d'utiliser le fichier local le plus récent.
      if (!existingDocument || (!existingDocument.file && doc.file)) {
        documentsByKey.set(key, doc)
      }
    }

    return Array.from(documentsByKey.values())
  }

  /**
   * Convertit tous les documents du test en base64.
   *
   * Les nouveaux fichiers sont convertis depuis leur objet File.
   * Les documents déjà existants sont récupérés via l'API de téléchargement base64.
   */
  const buildDocumentsBase64 = async (): Promise<string[]> => {
    const mergedDocuments = mergeDocuments()

    const documents = await Promise.all(
      mergedDocuments.map(async (doc) => {
        if (doc.file) {
          const base64 = await convertToBase64(doc.file)

          return `data:${doc.mimeType};base64,${base64}`
        }

        if (typeof doc.id === 'number') {
          const { base64 } = await fetchDocumentBase64({
            documentId: doc.id,
          }).unwrap()

          return base64
        }

        return null
      })
    )

    // Le type guard permet de retourner un string[] strictement typé.
    return documents.filter((document): document is string => document !== null)
  }

  /**
   * Construit la liste des modèles réellement sélectionnés par l'utilisateur.
   *
   * Cette méthode évite ensuite de dépendre de l'index des résultats pour savoir
   * quel modèle a produit quelle réponse.
   */
  const getSelectedModels = (
    data: PromptTestFormData
  ): PromptModelRequest[] => {
    return [
      data.modele1
        ? {
            key: 'modele1',
            modeleName: data.modele1,
          }
        : null,
      data.modele2
        ? {
            key: 'modele2',
            modeleName: data.modele2,
          }
        : null,
    ].filter((model): model is PromptModelRequest => model !== null)
  }

  /**
   * Exécute un appel de test de prompt pour un modèle donné.
   *
   * La durée est mesurée autour de l'appel API afin d'afficher le temps de réponse
   * associé à chaque modèle.
   */
  const runPromptRequest = async (
    model: PromptModelRequest,
    bodyBase: PromptBodyBase
  ): Promise<IModeleAnswer> => {
    const startTime = performance.now()

    const response: IPromptAskResponse = await askPrompt({
      ...bodyBase,
      modele: model.modeleName,
    }).unwrap()

    return {
      answer: response,
      time: performance.now() - startTime,
      modeleName: model.modeleName,
    }
  }

  /**
   * Soumet le formulaire de test du prompt.
   *
   * Cette méthode prépare le prompt final, convertit les documents,
   * lance les appels API pour les modèles sélectionnés, puis ajoute le résultat
   * dans la conversation de test.
   */
  const onSubmit: SubmitHandler<PromptTestFormData> = async (data) => {
    const idClient = currentUser?.idClient

    if (!idClient) {
      return
    }

    setIsLoading(true)

    const displayedValidationMessages = new Set<string>()

    try {
      const text = buildPromptText(promptValue, data.fields)

      const bodyBase: PromptBodyBase = {
        idClient,
        prompt: text,
        temperature: temperatureValue,
        webSearch: webSearchValue,
        documents: await buildDocumentsBase64(),
      }

      const selectedModels = getSelectedModels(data)

      const results = await Promise.allSettled(
        selectedModels.map((model) => runPromptRequest(model, bodyBase))
      )

      const answers: IModeleAnswer[] = []

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          answers.push(result.value)
          return
        }

        console.error('Erreur lors du test de prompt:', result.reason)
        displayValidationToastIfNeeded(
          result.reason,
          displayedValidationMessages
        )
      })

      setConversation((prev) => ({
        messages: [
          ...prev.messages,
          {
            type: UserType.USER,
            content: text,
            timestamp: Date.now(),
          },
          {
            type: UserType.ASSISTANT,
            content: t('prompts.form.promptResultText'),
            timestamp: Date.now(),
          },
          {
            answers,
          },
        ],
      }))
    } catch (error) {
      console.error('Erreur lors des appels API:', error)
      displayValidationToastIfNeeded(error, displayedValidationMessages)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Synchronise les variables détectées lorsque le contenu du prompt change.
   */
  useEffect(() => {
    setVariables(extractVariablesFromPrompt(promptValue))
  }, [promptValue])

  /**
   * Synchronise les champs dynamiques du formulaire avec les variables détectées.
   *
   * Attention : replace réinitialise les valeurs saisies lorsque la liste des variables change.
   */
  useEffect(() => {
    replace(variables.map((variable) => ({ name: variable, value: '' })))
  }, [variables, replace])

  /**
   * Synchronise le premier modèle de test avec le modèle configuré dans le formulaire principal.
   */
  useEffect(() => {
    if (modeleValue) {
      setValue('modele1', modeleValue)
    }
  }, [modeleValue, setValue])

  return {
    register,
    handleSubmit,
    fields,
    onSubmit,
    replace,
    getFormTestValues: getValues,
    modele1Field,
    variables,
    refreshVars,
    documentsTest,
    setDocumentsTest,
  }
}
